/**
 * Administração — página única do GERENTE do escritório:
 *   1. Identidade (logo + nome de exibição do kanban)
 *   2. Modelo de petição (papel timbrado geral, .docx)
 *   3. Usuários & acesso (mesmo conteúdo da página clássica, embutido)
 * Substitui o item "Usuários" no menu superior.
 */
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getXEscritorio } from '../api';
import { TopNav } from '../components/TopNav';
import { SeccaoSociosHonorarios } from '../components/financeiro/SeccaoSociosHonorarios';
import { useBranding, useMe } from '../hooks';
import { Usuarios } from './Usuarios';

async function jfetch(path: string, init?: RequestInit) {
  const r = await fetch(path, { credentials: 'include', ...init });
  if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail ?? `HTTP ${r.status}`);
  return r.json();
}

export function Administracao() {
  const { data: me } = useMe();
  const { data: branding } = useBranding();
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [logoBust, setLogoBust] = useState(0);
  const [tokenClaude, setTokenClaude] = useState('');
  const [claudeOk, setClaudeOk] = useState<boolean | null>(null);
  const fileLogo = useRef<HTMLInputElement>(null);
  const fileTpl = useRef<HTMLInputElement>(null);

  const ehTenantLegado = branding?.escritorio_id === 1;
  const podeAdministrar = me?.papel === 'admin' || me?.papel === 'gerente';

  async function upload(input: HTMLInputElement | null, endpoint: string, rotulo: string) {
    const f = input?.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('arquivo', f);
    setMsg(null);
    try {
      const r = await jfetch(`/api/escritorio/${endpoint}`, { method: 'POST', body: fd });
      setMsg(`✅ ${rotulo} atualizado (${Math.round((r.bytes ?? 0) / 1024)} KB)`);
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      setLogoBust(Date.now());
    } catch (e: any) {
      setMsg(`❌ ${rotulo}: ${e.message}`);
    } finally {
      if (input) input.value = '';
    }
  }

  async function salvarNome(nome: string) {
    if (!nome.trim() || nome === branding?.nome_exibicao) return;
    try {
      await jfetch('/api/escritorio/branding', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome_exibicao: nome.trim() }),
      });
      setMsg('✅ nome de exibição atualizado');
      queryClient.invalidateQueries({ queryKey: ['branding'] });
    } catch (e: any) { setMsg(`❌ nome: ${e.message}`); }
  }

  async function conectarClaude() {
    if (!tokenClaude.trim()) return;
    setMsg(null);
    try {
      await jfetch('/api/escritorio/claude-token', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenClaude.trim() }),
      });
      setTokenClaude(''); setClaudeOk(true);
      setMsg('✅ conta Claude conectada — a geração de peças usará a assinatura do escritório');
      queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (e: any) { setMsg(`❌ claude: ${e.message}`); }
  }

  async function desconectarClaude() {
    setMsg(null);
    try {
      await jfetch('/api/escritorio/claude-token', { method: 'DELETE' });
      setClaudeOk(false); setMsg('✅ conta Claude desconectada');
      queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (e: any) { setMsg(`❌ claude: ${e.message}`); }
  }

  const claudeConectado = claudeOk ?? !!me?.escritorio?.claude_conectado;

  const cardCls = 'bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 space-y-3';
  const btnCls = 'px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200';

  return (
    <div>
      <TopNav />
      <main className="max-w-screen-xl mx-auto p-4 space-y-6">
        <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">⚙️ Administração do escritório</h1>
        {msg && <div className="text-xs px-3 py-2 rounded bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-200">{msg}</div>}

        {podeAdministrar && (
          <div className="grid md:grid-cols-2 gap-4">
            {/* Identidade */}
            <div className={cardCls}>
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">🖼 Identidade visual</h2>
              {ehTenantLegado ? (
                <p className="text-xs text-gray-500">A identidade deste escritório é gerida pelo administrador do sistema.</p>
              ) : (
                <>
                  <div className="flex items-center gap-4">
                    <img
                      key={logoBust}
                      src={`/api/branding/logo?t=${logoBust}${getXEscritorio() ? `&escritorio=${getXEscritorio()}` : ''}`}
                      alt="logo atual"
                      className="h-12 w-auto rounded bg-white p-1 border border-gray-200 dark:border-gray-600"
                      onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2'; }}
                    />
                    <div className="space-y-1">
                      <button onClick={() => fileLogo.current?.click()} className={btnCls}>📤 Enviar logo (.png)</button>
                      <p className="text-[11px] text-gray-400">Aparece no topo do kanban. PNG, fundo transparente recomendado.</p>
                    </div>
                    <input ref={fileLogo} type="file" accept=".png" className="hidden"
                      onChange={() => upload(fileLogo.current, 'logo', 'Logo')} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Nome de exibição (kanban)</label>
                    <input
                      defaultValue={branding?.nome_exibicao ?? ''}
                      onBlur={(e) => salvarNome(e.target.value)}
                      className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Nome do escritório"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Modelo de petição */}
            <div className={cardCls}>
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">📄 Modelo de petição (papel timbrado)</h2>
              {ehTenantLegado ? (
                <p className="text-xs text-gray-500">O modelo deste escritório é gerido pelo administrador do sistema.</p>
              ) : (
                <>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Documento Word (.docx) com o cabeçalho/rodapé do escritório. Todas as petições
                    geradas pelo sistema serão renderizadas sobre este modelo.
                  </p>
                  <button onClick={() => fileTpl.current?.click()} className={btnCls}>📤 Enviar modelo (.docx)</button>
                  <input ref={fileTpl} type="file" accept=".docx" className="hidden"
                    onChange={() => upload(fileTpl.current, 'template', 'Modelo de petição')} />
                </>
              )}
            </div>
          </div>
        )}

        {/* Conta Claude do escritório */}
        {podeAdministrar && !ehTenantLegado && (
          <div className={cardCls}>
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                🤖 Conta Claude {claudeConectado ? '— 🟢 conectada' : '— ⚪ não conectada'}
              </h2>
              {claudeConectado && (
                <button onClick={desconectarClaude}
                  className="px-2 py-1 text-xs rounded border border-red-300/60 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                  Desconectar
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              A geração de peças com IA usa a <strong>assinatura Claude do seu escritório</strong>.
              Para conectar: instale o Claude Code (<code className="bg-gray-100 dark:bg-gray-900 px-1 rounded">claude.com/claude-code</code>),
              faça login com a conta do escritório, rode <code className="bg-gray-100 dark:bg-gray-900 px-1 rounded">claude setup-token</code> no
              terminal e cole abaixo o token gerado (começa com <code>sk-ant-oat</code>):
            </p>
            <div className="flex gap-2">
              <input value={tokenClaude} onChange={(e) => setTokenClaude(e.target.value)} type="password"
                placeholder="sk-ant-oat01-…"
                className="flex-1 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              <button onClick={conectarClaude} disabled={!tokenClaude.trim()}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm">
                Conectar
              </button>
            </div>
          </div>
        )}

        {/* Sócios & honorários — somente admin (controla a aba Financeiro) */}
        {me?.papel === 'admin' && <SeccaoSociosHonorarios cardCls={cardCls} />}

        {/* Usuários & acesso (conteúdo clássico embutido) */}
        <Usuarios embed />
      </main>
    </div>
  );
}
